import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Save, Loader2, Pencil, History, FileUp, Trash2, Download, Paperclip,
  AlertTriangle, ExternalLink, Package,
} from "lucide-react";
import { toast } from "sonner";
import LinesWorkspace from "@/components/order-edit/LinesWorkspace";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const CONFIRMED_STAGES_TEXT = "Sipariş üretim aşamasında — bu değişiklikler revizyon olarak kaydedilecek.";
const fmtD = (d) => (d ? new Date(String(d).length <= 10 ? `${d}T00:00:00` : d).toLocaleDateString("tr-TR") : "");

/**
 * Full order edit sheet with 3 tabs: Edit / Revisions / Documents.
 * Opened from OrdersList "Düzenle" button.
 */
export default function EditOrderSheet({ order, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [changeSummary, setChangeSummary] = useState("");
  const [tab, setTab] = useState("lines");
  const [dirty, setDirty] = useState(0);
  const [fx, setFx] = useState(null);
  const [discLimit, setDiscLimit] = useState(10);
  const [stageLabels, setStageLabels] = useState({});
  const loadFx = () => order && api.get(`/orders/${order.id}/fx-rates`).then(({ data }) => setFx(data)).catch(() => setFx(null));

  const isConfirmed = order && !["teklif", "siparis_onay"].includes(order.stage);

  useEffect(() => {
    if (!open || !order) return;
    setForm({
      product_name: order.product_name || "",
      quantity: order.quantity || 0,
      unit_price: order.unit_price || 0,
      due_date: order.due_date ? order.due_date.split("T")[0] : "",
      invoice_date: order.invoice_date ? order.invoice_date.split("T")[0] : "",
      priority: order.priority || "normal",
      notes: order.notes || "",
      delivery_term: order.delivery_term || "",
      payment_terms: order.payment_terms || "",
      size_breakdown: order.size_breakdown || {},
    });
    setChangeSummary("");
    setTab("lines");
    setDirty(0);
    loadTabs();
    loadFx();
    api.get("/settings/discount-limit").then(({ data }) => setDiscLimit(Number(data?.approval_limit_percent ?? 10))).catch(() => {});
    api.get("/meta/stages").then(({ data }) => setStageLabels(data?.labels || {})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id]);

  const loadTabs = async () => {
    if (!order) return;
    try {
      const [r, d] = await Promise.all([
        api.get(`/orders/${order.id}/revisions`),
        api.get(`/orders/${order.id}/documents`),
      ]);
      setRevisions(r.data || []);
      setDocuments(d.data || []);
    } catch (e) { /* ignore */ }
  };

  const patchForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Vade Tarihi — Satırlar sekmesinde sipariş genel bilgisi; mevcut full-update +
  // revizyon mekanizması REUSE (onaylı siparişte revizyon sebebi zorunlu)
  const dueDateOnlyChanged = form && (form.due_date || "") !== (order?.due_date ? order.due_date.split("T")[0] : "");
  const termsChanged = form && ((form.delivery_term || "") !== (order?.delivery_term || "")
    || (form.payment_terms || "") !== (order?.payment_terms || ""));
  const dueDateChanged = dueDateOnlyChanged || termsChanged;
  const saveDueDate = async () => {
    if (dueDateOnlyChanged && !form?.due_date) { toast.error("Vade tarihi seçin"); return; }
    if (isConfirmed && !changeSummary.trim()) { toast.error("Revizyon sebebi zorunludur"); return; }
    setSaving(true);
    try {
      const payload = {};
      if (dueDateOnlyChanged) payload.due_date = new Date(form.due_date).toISOString();
      if (termsChanged) {
        payload.delivery_term = (form.delivery_term || "").trim();
        payload.payment_terms = (form.payment_terms || "").trim();
      }
      if (isConfirmed) payload.change_summary = changeSummary.trim();
      await api.put(`/orders/${order.id}/full-update`, payload);
      toast.success(isConfirmed ? "Revizyon oluşturuldu — bilgiler güncellendi" : "Bilgiler güncellendi");
      onSaved?.();
      loadTabs();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // FT fatura tarihleri — PATCH /orders/{id}/invoice-dates REUSE (revizyon sebebi
  // gerekmez; satır/fiyat/miktar kilidi ayrı ve korunur). Gönderilmiş FT'de backend 409.
  const isInvoice = order?.stage === "fatura";
  const ftDatesChanged = form && isInvoice && (
    (form.due_date || "") !== (order?.due_date ? order.due_date.split("T")[0] : "")
    || (form.invoice_date || "") !== (order?.invoice_date ? order.invoice_date.split("T")[0] : "")
  );
  const saveInvoiceDates = async () => {
    if (!form?.invoice_date) { toast.error("Fatura tarihi seçin"); return; }
    if (form.due_date && form.invoice_date && form.due_date < form.invoice_date) {
      toast.error("Vade tarihi fatura tarihinden önce olamaz."); return;
    }
    setSaving(true);
    try {
      await api.patch(`/orders/${order.id}/invoice-dates`, {
        invoice_date: form.invoice_date,
        due_date: form.due_date || "",
      });
      toast.success("Fatura tarihleri güncellendi");
      onSaved?.();
      loadTabs();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (isConfirmed && !changeSummary.trim()) {
      toast.error("Revizyon sebebi zorunludur");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        product_name: form.product_name,
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price),
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        priority: form.priority,
        notes: form.notes,
      };
      if (isConfirmed) payload.change_summary = changeSummary.trim();
      await api.put(`/orders/${order.id}/full-update`, payload);
      toast.success(isConfirmed ? "Revizyon oluşturuldu" : "Sipariş güncellendi");
      onSaved?.();
      loadTabs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", "");
      await api.post(`/orders/${order.id}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} yüklendi`);
      loadTabs();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeDoc = async (doc) => {
    if (!window.confirm(`${doc.original_filename} silinsin mi?`)) return;
    try {
      await api.delete(`/orders/${order.id}/documents/${doc.id}`);
      loadTabs();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadDoc = async (doc) => {
    try {
      const resp = await api.get(`/orders/${order.id}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!order) return null;

  const guardedOpenChange = (v) => {
    if (!v && dirty > 0 && !window.confirm(`${dirty} satırda kaydedilmemiş değişiklik var. Çıkılsın mı?`)) return;
    onOpenChange(v);
  };
  const fxChips = ["EUR", "USD"].filter((c) => fx?.[c]?.rate);
  const Info = ({ label, value, testId, hideSm }) => (
    <div className={`min-w-0 ${hideSm ? "max-sm:hidden" : ""}`} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-[0.14em] font-mono text-slate-500">{label}</div>
      <div className="text-[13px] text-slate-200 truncate">{value || "—"}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={guardedOpenChange}>
      <DialogContent data-testid="edit-order-sheet"
        onEscapeKeyDown={(e) => { if (document.activeElement?.dataset?.cell !== undefined || dirty > 0) { e.preventDefault(); if (dirty > 0 && document.activeElement?.dataset?.cell === undefined) guardedOpenChange(false); } }}
        onInteractOutside={(e) => e.preventDefault()}
        className="bg-slate-950 border border-slate-800 text-slate-100 p-0 gap-0 flex flex-col overflow-hidden
          w-[95vw] max-w-[1840px] h-[92vh] sm:rounded-md
          max-sm:w-screen max-sm:max-w-none max-sm:h-[100dvh] max-sm:rounded-none max-sm:border-0">
        <DialogHeader className="shrink-0 px-4 sm:px-6 pt-3.5 pb-0 border-b border-slate-800 bg-slate-900/50 space-y-0 text-left">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pr-8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">{isInvoice ? "Fatura Düzenle" : "Sipariş Düzenle"}</span>
            <DialogTitle className="text-slate-50 tracking-tight text-lg font-semibold font-mono" data-testid="ws-order-no">{order.order_no}</DialogTitle>
            <Badge variant="outline" className="rounded-sm border-slate-700 text-slate-300 text-[10px] uppercase tracking-wider" data-testid="ws-stage">
              {stageLabels[order.stage] || order.stage}
            </Badge>
            {isConfirmed && !isInvoice && (
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-300" data-testid="ws-confirmed-note">
                <AlertTriangle className="w-3.5 h-3.5" /> Üretimde — değişiklikler revizyon olarak kaydedilir
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-x-8 gap-y-2 py-2.5">
            <Info label="Müşteri" value={order.customer_name} testId="ws-customer" />
            <Info label={isInvoice ? "Fatura Tarihi" : "Tarih"} value={fmtD(isInvoice ? order.invoice_date || order.created_at : order.created_at)} testId="ws-date" />
            <Info hideSm label={isInvoice ? "Vade" : "Termin"} value={isInvoice ? fmtD(order.due_date) : (order.delivery_term || fmtD(order.due_date))} testId="ws-termin" />
            {fxChips.length > 0 && (
              <div className="min-w-0 col-span-2 sm:col-span-1 max-sm:hidden" data-testid="ws-fx">
                <div className="text-[9px] uppercase tracking-[0.14em] font-mono text-slate-500">Kur (sipariş snapshot)</div>
                <div className="flex flex-wrap gap-x-3 text-[12px] font-mono text-slate-300">
                  {fxChips.map((c) => (
                    <span key={c} data-testid={`ws-fx-${c}`} title={fx[c].source === "order" ? "Siparişte kayıtlı kur" : "Siparişte bu para biriminde kayıt yok — günün TCMB kuru"}>
                      {c}/TRY <b className="text-slate-100">{Number(fx[c].rate).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</b>
                      <span className="text-slate-500"> · {fx[c].date ? fmtD(fx[c].date) : "—"}{fx[c].source !== "order" ? " (günün)" : ""}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {isConfirmed && !isInvoice && (
              <div className="min-w-[260px] flex-1 max-w-md col-span-2">
                <div className="text-[9px] uppercase tracking-[0.14em] font-mono text-amber-400">Revizyon Sebebi *</div>
                <input value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} data-testid="lines-change-summary"
                  placeholder="Örn: Müşteri talebiyle fiyat güncellemesi"
                  className="w-full h-7 bg-slate-950 border border-amber-900/60 rounded-sm px-2 text-xs outline-none focus:border-amber-500" />
              </div>
            )}
          </div>
          <div className="flex gap-1 -mb-px overflow-x-auto">
            {[["lines", "Satırlar", Package], ["info", "Genel Bilgiler", Pencil], ["revisions", `Revizyonlar (${revisions.length})`, History], ["docs", `Evrak (${documents.length})`, Paperclip]].map(([k, lbl, Icon]) => (
              <button key={k} type="button" onClick={() => setTab(k)} data-testid={`edit-tab-${k}`}
                className={`px-3 py-2 text-xs inline-flex items-center gap-1.5 border-b-2 whitespace-nowrap transition-colors
                  ${tab === k ? "border-blue-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                <Icon className="w-3.5 h-3.5" /> {lbl}
                {k === "lines" && dirty > 0 && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-400" />}
              </button>
            ))}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col">
          <div className={`${tab === "lines" ? "flex" : "hidden"} flex-1 min-h-0 flex-col`}>
            <LinesWorkspace order={order} fx={fx} readOnly={isInvoice} confirmed={isConfirmed} changeSummary={changeSummary}
              discLimit={discLimit} onDirtyChange={setDirty}
              onSaved={() => { onSaved?.(); loadTabs(); loadFx(); }} />
          </div>
          {tab !== "lines" && form && (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5 space-y-3 max-w-5xl">
              {tab === "info" && (
                <>{isInvoice ? (
                  <div className="flex flex-wrap items-end gap-2 rounded-sm border border-slate-800 bg-slate-900/40 px-3 py-2.5" data-testid="ft-dates-block">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Fatura Tarihi</Label>
                      <Input type="date" data-testid="lines-invoice-date" value={form.invoice_date || ""}
                        disabled={order?.bh_send_status === "sent"}
                        onChange={(e) => patchForm({ invoice_date: e.target.value })}
                        className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs w-40" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Vade Tarihi</Label>
                      <Input type="date" data-testid="lines-due-date" value={form.due_date || ""}
                        disabled={order?.bh_send_status === "sent"}
                        onChange={(e) => patchForm({ due_date: e.target.value })}
                        className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs w-40" />
                    </div>
                    <Button size="sm" onClick={saveInvoiceDates}
                      disabled={saving || !ftDatesChanged || order?.bh_send_status === "sent"}
                      data-testid="lines-invoice-dates-save"
                      className="h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Kaydet</>}
                    </Button>
                    {order?.bh_send_status === "sent" && (
                      <span className="text-[10px] font-mono text-emerald-400 self-center" data-testid="ft-dates-locked">
                        BH'a gönderildi — tarihler kilitli
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-end gap-2 rounded-sm border border-slate-800 bg-slate-900/40 px-3 py-2.5">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Vade Tarihi</Label>
                      <Input type="date" data-testid="lines-due-date" value={form.due_date || ""}
                        onChange={(e) => patchForm({ due_date: e.target.value })}
                        className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs w-40" />
                    </div>
                    <div className="space-y-1 flex-1 min-w-[160px]">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Termin</Label>
                      <Input data-testid="lines-delivery-term" value={form.delivery_term || ""}
                        placeholder="Örn: 15 iş günü"
                        onChange={(e) => patchForm({ delivery_term: e.target.value })}
                        className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs" />
                    </div>
                    <div className="space-y-1 flex-1 min-w-[160px]">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Ödeme Şekli</Label>
                      <Input data-testid="lines-payment-terms" value={form.payment_terms || ""}
                        placeholder="Örn: %50 Sipariş / %50 Teslimat"
                        onChange={(e) => patchForm({ payment_terms: e.target.value })}
                        className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs" />
                    </div>
                    <Button size="sm" onClick={saveDueDate}
                      disabled={saving || !dueDateChanged}
                      data-testid="lines-due-date-save"
                      className="h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Kaydet</>}
                    </Button>
                  </div>
                )}</>
              )}
              {tab === "revisions" && (
                <div className="space-y-3">
                {revisions.length === 0 ? (
                  <Card className="bg-slate-900 border-slate-800 rounded-sm p-8 text-center text-sm text-slate-500">
                    Bu sipariş için revizyon geçmişi yok.
                  </Card>
                ) : revisions.map((r) => (
                  <Card key={r.id} className="bg-slate-900 border-slate-800 rounded-sm p-4" data-testid={`revision-${r.revision_no}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-blue-950/40 border-blue-900 text-blue-300 rounded-sm text-[10px] uppercase tracking-widest">
                        Rev {r.revision_no}
                      </Badge>
                      <div className="text-xs text-slate-500 font-mono">
                        {new Date(r.created_at).toLocaleString("tr-TR")} · {r.changed_by}
                      </div>
                    </div>
                    <RevisionChanges r={r} />
                  </Card>
                ))}

                </div>
              )}
              {tab === "docs" && (
                <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-400">PDF, Word, Excel, JPG, PNG, SVG, AI, CDR, PSD, ZIP (max 25MB)</div>
                  <label className="cursor-pointer">
                    <input type="file" hidden onChange={uploadFile}
                      data-testid="upload-doc-input"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.webp,.gif,.svg,.ai,.cdr,.psd,.eps,.zip,.rar,.7z,.txt" />
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-950/30 hover:bg-blue-950/60 text-blue-300 text-xs uppercase tracking-wider font-semibold cursor-pointer">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                      Evrak Yükle
                    </span>
                  </label>
                </div>
                {documents.length === 0 ? (
                  <Card className="bg-slate-900 border-slate-800 rounded-sm p-8 text-center text-sm text-slate-500">
                    Henüz evrak yüklenmedi.
                  </Card>
                ) : (
                  <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent border-slate-800">
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Dosya</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Yüklenme</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Boyut</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {documents.map((d) => (
                          <TableRow key={d.id} className="border-slate-800" data-testid={`doc-row-${d.id}`}>
                            <TableCell className="text-sm">
                              <div className="font-medium text-slate-100 truncate max-w-[220px]">{d.original_filename}</div>
                              <div className="text-[10px] text-slate-500 font-mono">{d.uploaded_by}</div>
                            </TableCell>
                            <TableCell className="text-xs text-slate-400 font-mono">
                              {new Date(d.created_at).toLocaleDateString("tr-TR")}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-slate-400">{fmtSize(d.size)}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)}
                                data-testid={`download-doc-${d.id}`}
                                className="text-slate-400 hover:text-slate-100 rounded-sm">
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => removeDoc(d)}
                                data-testid={`delete-doc-${d.id}`}
                                className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                )}

                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


/** Revizyon kartı gövdesi — sebep + alan bazlı eski → yeni değişiklik listesi. */
function RevisionChanges({ r }) {
  const prev = r.previous_snapshot || {};
  const next = r.new_snapshot || {};
  const fmtVal = (v) => (typeof v === "number" ? fmt(v) : String(v ?? "—"));
  const changes = next.changes && typeof next.changes === "object" ? next.changes : null;
  const reason = prev.reason || r.change_summary || "";

  return (
    <div className="space-y-1.5">
      {reason && (
        <div className="text-xs text-slate-300" data-testid={`revision-reason-${r.revision_no}`}>
          <span className="text-[10px] uppercase font-mono text-slate-500">Sebep: </span>{reason}
        </div>
      )}
      {changes ? (
        <div className="border border-slate-800 rounded-sm p-2 bg-slate-950/50" data-testid={`revision-changes-${r.revision_no}`}>
          {next.product_name && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-1">
              {next.product_name}
              {next.action === "add" && " · satır eklendi"}
              {next.action === "delete" && " · satır silindi"}
            </div>
          )}
          {Object.entries(changes).map(([lbl, nv]) => (
            <div key={lbl} className="text-xs font-mono text-slate-300" data-testid={`revision-diff-${r.revision_no}-${lbl}`}>
              {lbl}: <span className="text-red-400">{fmtVal(prev.changes?.[lbl])}</span>
              {" → "}
              <span className="text-emerald-400">{fmtVal(nv)}</span>
            </div>
          ))}
          {prev.order_total_price !== undefined && next.order_total_price !== undefined
            && Number(prev.order_total_price) !== Number(next.order_total_price) && (
            <div className="text-xs font-mono text-slate-300 mt-1.5 pt-1.5 border-t border-slate-800">
              Genel Toplam: <span className="text-red-400">₺{fmtVal(prev.order_total_price)}</span>
              {" → "}
              <span className="text-emerald-400">₺{fmtVal(next.order_total_price)}</span>
            </div>
          )}
        </div>
      ) : (
        // Legacy düz revizyon (Düzenle sekmesi alanları)
        <div className="border border-slate-800 rounded-sm p-2 bg-slate-950/50" data-testid={`revision-changes-${r.revision_no}`}>
          {Object.keys(next).filter((k) => k !== "updated_at").map((k) => (
            <div key={k} className="text-xs font-mono text-slate-300">
              {k}: <span className="text-red-400">{fmtVal(prev[k])}</span>
              {" → "}
              <span className="text-emerald-400">{fmtVal(next[k])}</span>
            </div>
          ))}
          {Object.keys(next).filter((k) => k !== "updated_at").length === 0 && (
            <div className="text-xs text-slate-500">{r.change_summary}</div>
          )}
        </div>
      )}
    </div>
  );
}